import { useCallback } from 'react';
import '../../../../css/tabs.less';
import { ClipBoardProvider } from '../../ClipBoardProvider';
import { SYSTEM_ROLES } from '../../constants/system';
import { useRouting, useTabRouting } from '../../hooks/route';
import { useNotificationDispatch } from '../notifications/NotificationProvider';
import TabContent from './TabContent';
import Tabs from './Tabs';
import { useTabs, useTabsDispatch } from './TabsProvider';
import type { UserTenantResponseAPI } from '../../types/user';
import { useIsSingleTenant } from '../../hooks/organization';

type TabContentProps = {
    isTenantSelectorOpen: boolean;
    tenant: UserTenantResponseAPI;
};

export const TabsSection = ({ isTenantSelectorOpen, tenant }: TabContentProps) => {
    const { tabs, activeTabKey } = useTabs();
    const dispatchTabs = useTabsDispatch();
    const { closeTab, selectTab, reOrderTabs } = useRouting();
    const isSingleTenant = useIsSingleTenant(tenant);

    const dispatchNotification = useNotificationDispatch();

    const onError = useCallback(
        (error: unknown) => {
            dispatchNotification({
                type: 'notifyError',
                payload: { error },
            });
        },
        [dispatchNotification],
    );

    let role = tenant?.role;
    let isAdministrator = role === SYSTEM_ROLES.administrator.developerName;

    if (isSingleTenant) {
        if (
            tenant?.role === SYSTEM_ROLES.administrator.developerName ||
            tenant?.organization?.role === 'organization_administrator'
        ) {
            role = SYSTEM_ROLES.administrator.developerName;
            isAdministrator = true;
        }
    }

    const environmentsIsOn = tenant?.tenantSettings?.environments ?? false;

    /**
     * Subscribe to route changes and open tabs based on the url
     */
    useTabRouting(role, environmentsIsOn, isAdministrator, onError, dispatchTabs);

    return (
        <div className="tabs flex-column">
            <ClipBoardProvider>
                <Tabs
                    tabs={tabs}
                    activeTabKey={activeTabKey}
                    onSelect={selectTab}
                    onClose={closeTab}
                    onReOrderTabs={reOrderTabs}
                    dispatchTabs={dispatchTabs}
                    accountMode={!!isSingleTenant}
                />
                <div className="tab-content-wrapper">
                    {tabs.map((tab) => (
                        <TabContent
                            tenant={tenant}
                            tab={tab}
                            isActive={tab.key === activeTabKey}
                            key={tab.key}
                            isTenantSelectorOpen={isTenantSelectorOpen}
                        />
                    ))}
                </div>
            </ClipBoardProvider>
        </div>
    );
};

export default TabsSection;
